import { useTaxIntegrationProvider } from 'lib/hooks/useTaxIntegrationProvider.ts'
import { useLoadAllItemsQuery, useQuery } from '@sequencehq/api/utils'
import {
  Dashboardv99990101Api,
  dashboardv99990101Client
} from '@sequencehq/api/dashboard/v99990101'
import { Dashboardv99990101CommonModels } from '@sequencehq/api/dashboard/v99990101'
import { TAX_INTEGRATION_PROVIDERS } from 'lib/hooks/useTaxIntegrationProvider.ts'
import { TaxStatus } from 'modules/Customers/types'

type UseTaxCategoryValidationProps = {
  taxCategoryId: string
  customerCountry?: string
  customerTaxStatus: Dashboardv99990101CommonModels.Customers.CustomerTaxStatus
}

type UseTaxCategoryValidation = (props: UseTaxCategoryValidationProps) =>
  | {
      valid: boolean
      provider: typeof TAX_INTEGRATION_PROVIDERS.SEQUENCE
      taxRatesForCountry: Array<Dashboardv99990101Api.GetTaxRates.TaxRate>
    }
  | {
      valid: boolean
      provider: typeof TAX_INTEGRATION_PROVIDERS.AVALARA
      taxRatesForCountry: undefined
    }
  | {
      valid: boolean
      provider: typeof TAX_INTEGRATION_PROVIDERS.NUMERAL
      taxRatesForCountry: undefined
    }
  | {
      valid: boolean
      provider: typeof TAX_INTEGRATION_PROVIDERS.ANROK
      taxRatesForCountry: undefined
    }
  | {
      valid: boolean
      provider: typeof TAX_INTEGRATION_PROVIDERS.SPHERE
      taxRatesForCountry: undefined
    }

const exemptTaxStatuses: TaxStatus[] = ['TAX_EXEMPT', 'REVERSE_CHARGED']

export const useTaxCategoryValidation: UseTaxCategoryValidation = ({
  taxCategoryId,
  customerCountry,
  customerTaxStatus
}) => {
  const { isAvalara, isSequence, isNumeral, isAnrok, isSphere } =
    useTaxIntegrationProvider()
  const { data: taxRatesData, isLoading } = useLoadAllItemsQuery(
    dashboardv99990101Client.getTaxRates,
    undefined,
    { enabled: isSequence }
  )

  const { data: avalaraMappingsData } = useQuery(
    dashboardv99990101Client.getAllTaxCategoriesWithAvalaraMappings,
    undefined,
    {
      enabled: isAvalara
    }
  )

  const { data: numeralMappingsData } = useQuery(
    dashboardv99990101Client.getAllTaxCategoriesWithNumeralMappings,
    undefined,
    {
      enabled: isNumeral
    }
  )

  const { data: anrokMappingsData } = useQuery(
    dashboardv99990101Client.getAllTaxCategoriesWithAnrokMappings,
    undefined,
    {
      enabled: isAnrok
    }
  )

  // Sphere works out tax itself, so there's no built-in rate to check,
  // whatever the customer's tax status.
  if (isSphere) {
    return {
      valid: true,
      provider: TAX_INTEGRATION_PROVIDERS.SPHERE,
      taxRatesForCountry: undefined
    }
  }

  if (exemptTaxStatuses.includes(customerTaxStatus)) {
    if (isAvalara) {
      return {
        valid: true,
        provider: TAX_INTEGRATION_PROVIDERS.AVALARA
      }
    }

    if (isNumeral) {
      return {
        valid: true,
        provider: TAX_INTEGRATION_PROVIDERS.NUMERAL
      }
    }

    if (isAnrok) {
      return {
        valid: true,
        provider: TAX_INTEGRATION_PROVIDERS.ANROK
      }
    }

    return {
      valid: true,
      provider: TAX_INTEGRATION_PROVIDERS.SEQUENCE,
      taxRatesForCountry: []
    }
  }

  if (isAvalara) {
    const avalaraMappings = avalaraMappingsData?.items ?? []
    const mappedTaxCategory = avalaraMappings.find(
      mapping => mapping.taxCategoryId === taxCategoryId
    )

    if (!mappedTaxCategory?.avalaraTaxCode) {
      return {
        valid: false,
        provider: TAX_INTEGRATION_PROVIDERS.AVALARA
      }
    }

    return {
      valid: true,
      provider: TAX_INTEGRATION_PROVIDERS.AVALARA
    }
  }

  if (isNumeral) {
    const numeralMappings = numeralMappingsData?.items ?? []
    const mappedTaxCategory = numeralMappings.find(
      mapping => mapping.taxCategoryId === taxCategoryId
    )

    if (!mappedTaxCategory?.numeralProductCategory) {
      return {
        valid: false,
        provider: TAX_INTEGRATION_PROVIDERS.NUMERAL
      }
    }

    return {
      valid: true,
      provider: TAX_INTEGRATION_PROVIDERS.NUMERAL
    }
  }

  if (isAnrok) {
    const anrokMappings = anrokMappingsData?.items ?? []
    const mappedTaxCategory = anrokMappings.find(
      mapping => mapping.taxCategoryId === taxCategoryId
    )

    if (!mappedTaxCategory?.anrokTaxCategoryId) {
      return {
        valid: false,
        provider: TAX_INTEGRATION_PROVIDERS.ANROK
      }
    }

    return {
      valid: true,
      provider: TAX_INTEGRATION_PROVIDERS.ANROK
    }
  }

  // No customer address means no country to match a tax rate against, so the
  // line item can't be validated and the save is blocked. The tax-rates modal
  // opens with no country preselected, letting the operator pick one and add a
  // rate rather than quietly sending an untaxed invoice.
  if (!customerCountry) {
    return {
      valid: false,
      provider: TAX_INTEGRATION_PROVIDERS.SEQUENCE,
      taxRatesForCountry: []
    }
  }

  const taxRates = taxRatesData?.pages.flatMap(page => page.items) ?? []
  const taxRatesForCustomerCountry = taxRates.filter(
    ({ country }) => country === customerCountry
  )
  const taxCategoryHasTaxRate = taxRatesForCustomerCountry.some(
    taxRate => taxRate.taxCategoryId === taxCategoryId
  )

  const isValid = !isLoading && taxCategoryHasTaxRate

  return {
    valid: isValid,
    provider: TAX_INTEGRATION_PROVIDERS.SEQUENCE,
    taxRatesForCountry: taxRatesForCustomerCountry
  }
}
